import * as THREE from "three";
import { sharedRenderer, gpuTag, weakGpu } from "./renderer.js";
import { cosmeticValue } from "@neon-purge/shared";
import { Avatar } from "./avatar.js";
import { api } from "../net/api.js";
import { perfBeacon } from "../net/clientlog.js";

/**
 * Animated 3D menu backdrop: the player's own character (cosmetics + custom
 * skin) rotating on a glowing neon dais over an infinite grid. Runs on the
 * game canvas behind the menu UI. Cheap and self-contained.
 */
export class MenuBackground {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private avatar: Avatar;
  private lastTauntT = 0;
  private daisGlow: THREE.Mesh;
  private running = false;
  private t = 0;
  private raf = 0;

  constructor(private canvas: HTMLCanvasElement) {
    this.renderer = sharedRenderer(canvas); // one shared GPU context for the whole tab
    this.applySize();

    this.scene.background = new THREE.Color("#05060a");
    this.scene.fog = new THREE.Fog("#05060a", 12, 40);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 100);
    // frame the character in the left third: look-target sits to its right so
    // the avatar (at origin) renders on screen-left, clear of the menu panel
    this.camera.position.set(0.4, 2.3, 6.4);
    this.camera.lookAt(2.6, 1.3, 0);

    this.scene.add(new THREE.HemisphereLight("#4a6a9a", "#0a0c14", 2.2));
    const key = new THREE.PointLight("#00f0ff", 40, 30);
    key.position.set(3, 4, 4);
    this.scene.add(key);
    const rim = new THREE.PointLight("#ff2ec4", 30, 30);
    rim.position.set(-4, 3, -2);
    this.scene.add(rim);

    // neon grid floor
    const grid = new THREE.GridHelper(80, 80, "#00f0ff", "#12233a");
    (grid.material as THREE.Material).transparent = true;
    (grid.material as THREE.Material).opacity = 0.35;
    this.scene.add(grid);

    // glowing dais
    const dais = new THREE.Mesh(
      new THREE.CylinderGeometry(1.4, 1.6, 0.2, 32),
      new THREE.MeshBasicMaterial({ color: "#0a1420" }),
    );
    dais.position.y = 0.1;
    this.scene.add(dais);
    this.daisGlow = new THREE.Mesh(
      new THREE.RingGeometry(1.5, 1.75, 48),
      new THREE.MeshBasicMaterial({
        color: "#00f0ff",
        transparent: true,
        opacity: 0.8,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
      }),
    );
    this.daisGlow.rotation.x = -Math.PI / 2;
    this.daisGlow.position.y = 0.21;
    this.scene.add(this.daisGlow);

    // the player's avatar
    const cos = api.account?.cosmetics;
    this.avatar = new Avatar(this.scene, {
      bodyColor: cosmeticValue("bodySkin", cos?.bodySkin),
      glowColor: cosmeticValue("glowColor", cos?.glowColor),
      headgear: cos?.headgear && cos.headgear !== "head_none" ? cosmeticValue("headgear", cos.headgear) : "",
      skinUrl: api.account?.skinUrl,
    });
    this.avatar.setWeapon("none"); // arms along the body on the landing page
    this.avatar.update(0, 0.2, 0, 0, false);

    window.addEventListener("resize", this.onResize);
  }

  /** A backdrop behind a UI panel doesn't need retina density: 1.5x on capable
   *  GPUs, 1x on integrated ones (a HiDPI laptop at 2x paid 4x the pixels). */
  private applySize(): void {
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, weakGpu() ? 1 : 1.5));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  private onResize = (): void => {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.applySize();
  };

  start(): void {
    if (this.running) return;
    this.running = true;
    this.menuLast = 0; // resuming from a park: no time jump, no stale frame gap
    this.lastRaf = 0;
    this.onResize(); // the window (or a match) may have resized the shared renderer meanwhile
    this.loop();
  }

  /** Stop rendering but KEEP the scene, so returning to the main menu from a
   *  sub-screen (Play, Settings, Cosmetics…) is instant instead of rebuilding
   *  the avatar, its textures and materials every time. dispose via stop(). */
  pause(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  private menuEma = 16;
  private menuLast = 0;
  private menuTick = 0;
  private lastRaf = 0;
  private rafEma = 1000 / 60;
  private frameNo = 0;
  // The backdrop is a slow turntable — the heaviest thing on the menu (skipped
  // entirely on TV). Rendering it at the full display rate burns GPU/battery on
  // weak laptops sitting in menus, and on 144/240 Hz panels it also ran the
  // animation FASTER (the old `t += 0.016` was per-frame, not per-second, so the
  // character spun ~2.4× fast and taunted 2.4× as often at 144 Hz). Cap the
  // render cadence and drive the animation off real elapsed time so it looks
  // identical everywhere.
  // Render on an EVEN divisor of the display refresh: every frame up to
  // ~60 fps (every 2nd on a 120/144 Hz panel, every 2nd at 60 Hz on integrated
  // GPUs -> a steady 30). The old fixed 40 fps cap judders on a 60 Hz screen
  // (frames alternately held 1 and 2 vsyncs) — that read as menu "lag".
  // Animation runs off real elapsed time, so it looks identical everywhere.
  private loop = (): void => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.loop);
    const nowMs = performance.now();
    if (this.lastRaf) {
      const d = nowMs - this.lastRaf;
      if (d > 2 && d < 100) this.rafEma += (d - this.rafEma) * 0.05;
    }
    this.lastRaf = nowMs;
    const targetMs = weakGpu() ? 1000 / 30 : 1000 / 60;
    const div = Math.max(1, Math.round(targetMs / this.rafEma));
    if (++this.frameNo % div !== 0) return;
    const since = this.menuLast ? nowMs - this.menuLast : this.rafEma;
    // measure the real menu render interval for the perf beacon
    if (this.menuLast) this.menuEma += (Math.min(100, since) - this.menuEma) * 0.05;
    this.menuLast = nowMs;
    if ((this.menuTick = (this.menuTick + 1) % 120) === 0) {
      const info = (this.renderer.info.render);
      perfBeacon(gpuTag(), "menu", `frame=${this.menuEma.toFixed(1)}ms calls=${info.calls} tris=${info.triangles}`);
    }
    // time-based, clamped so a backgrounded/refocused tab doesn't lurch
    this.t += Math.min(0.05, since / 1000);
    // the dais character shows off every ~9s (random taunt pose)
    if (this.t - this.lastTauntT > 9 + Math.random() * 4) {
      this.lastTauntT = this.t;
      this.avatar.playTaunt(Math.floor(Math.random() * 5));
    }
    // slow turntable rotation + a gentle idle sway of the legs/arms
    const yaw = this.t * 0.5;
    const sway = Math.sin(this.t * 1.5) * 0.06;
    this.avatar.group.rotation.y = yaw;
    this.avatar.group.position.y = 0.2 + Math.sin(this.t * 1.2) * 0.02;
    this.avatar.group.rotation.z = sway * 0.15;
    (this.daisGlow.material as THREE.MeshBasicMaterial).opacity = 0.55 + Math.sin(this.t * 2) * 0.25;
    this.camera.position.x = 0.4 + Math.sin(this.t * 0.15) * 0.3;
    this.camera.lookAt(2.6, 1.3, 0);
    this.renderer.render(this.scene, this.camera);
  };

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
    window.removeEventListener("resize", this.onResize);
    this.avatar.dispose(this.scene);
    this.scene.traverse((o) => {
      const m = o as THREE.Mesh;
      m.geometry?.dispose();
      const mats = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
      for (const mm of mats) mm.dispose();
    });
    this.scene.clear();
    // shared context stays alive for the match/next menu — never disposed here
  }
}
